import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { Order } from '../../../../models/orderModel';
import { MyConfigService } from '../../../../services/my-config.service';

interface ModuleData<T = any> {
  data: T;
  lastUpdated: Date;
}

interface OrganizationData {
  [moduleKey: string]: ModuleData;
}

interface AppState {
  currentOrg: MyConfigOrganization | null;
  orgData: {
    [orgId: string]: OrganizationData;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BackOffice24StatesService {
  private state = new BehaviorSubject<AppState>({
    currentOrg: null,
    orgData: {}
  });

  // Public observables create the observables for the current organization and data under each
  currentOrganization$ = this.state.pipe(
    map(state => state.currentOrg),
    distinctUntilChanged()
  );

  get orders$(): Observable<ModuleData<Order[]> | null> {
    return this.getModule<ModuleData<Order[]>>('orders');
  }

  setOrders(orders: Order[]): void {
    this.setModule<Order[]>('orders', orders);
  }

  get ordersAS$(): Observable<ModuleData<Order[]> | null> {
    return this.getModule<ModuleData<Order[]>>('ordersAS');
  }

  setOrdersAS(ordersAS: Order[]): void {
    this.setModule<Order[]>('ordersAS', ordersAS);
  }

  // Add the config service to constructor
  constructor(private myConfigService: MyConfigService) {}

  // Get module data for current org
  getModule<T>(moduleKey: string): Observable<T | null> {
    return this.state.pipe(
      map(state => {
        if (!state.currentOrg) return null;
        return state.orgData[state.currentOrg.organizationId]?.[moduleKey] as T ?? null;
      }),
      distinctUntilChanged()
    );
  }

  // Set module data for current org
  private setModule<T>(moduleKey: string, data: T): void {
    const state = this.state.value;
    if (!state.currentOrg) return;

    const orgId = state.currentOrg.organizationId;

    this.state.next({

      ...state,

      orgData: {
        ...state.orgData,

        [orgId]: {
          ...state.orgData[orgId],
          [moduleKey]: {
            data,
            lastUpdated: new Date()
          }

        }
      }

    });
  }

  // Set current organization
  setCurrentOrg(org: MyConfigOrganization | null): void {
    if (org === this.state.value.currentOrg) return;

    this.state.next({
      ...this.state.value,
      currentOrg: org
    });
  }

  // Clear organization data
  clearOrgData(orgId: string): void {
    const state = this.state.value;
    const { [orgId]: _, ...restOrgs } = state.orgData;

    this.state.next({
      ...state,
      orgData: restOrgs
    });
  }

  // For backward compatibility
  // updateOrders(orders: Order[], ordersAS: Order[]): void {
  //   this.setModule('orders', orders);
  //   this.setModule('ordersAS', ordersAS);
  // }

  fetchOrganization(orgId: string): MyConfigOrganization | null {
    const org = this.myConfigService.getMyConfigOrganizationById(orgId.toString().toLowerCase());
    if (org) {
      this.setCurrentOrg(org);
    }
    return org;
  }

  // initializeOrgData(){
  //   this.state.next({
  //     ...this.state.value,
  //     orgData: this.myConfigService.myConfig.organizations.reduce((acc, org) => {
  //       acc[org.organizationId] = {};
  //       return acc;
  //     }, {} as { [orgId: string]: OrganizationData })
  //   });
  // }
}
